import { useRef } from 'react';
import type { TabsProps } from './Tabs.types';
import './Tabs.scss';

export default function Tabs({ value, onChange, items, ariaLabel, className }: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const move = (delta: number) => {
    const enabled = items.filter(i => !i.disabled);
    const idx = enabled.findIndex(i => i.value === value);
    const next = enabled[(idx + delta + enabled.length) % enabled.length];
    if (next) {
      onChange(next.value);
      window.setTimeout(() => {
        listRef.current?.querySelector<HTMLButtonElement>(`[data-value="${next.value}"]`)?.focus();
      }, 0);
    }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
    else if (e.key === 'Home') {
      e.preventDefault();
      const first = items.find(i => !i.disabled);
      if (first) onChange(first.value);
    } else if (e.key === 'End') {
      e.preventDefault();
      const last = [...items].reverse().find(i => !i.disabled);
      if (last) onChange(last.value);
    }
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={ariaLabel}
      onKeyDown={onKey}
      className={['tabs', className || ''].filter(Boolean).join(' ')}
    >
      {items.map(it => {
        const selected = it.value === value;
        return (
          <button
            key={it.value}
            type="button"
            role="tab"
            data-value={it.value}
            aria-selected={selected}
            disabled={it.disabled}
            tabIndex={selected ? 0 : -1}
            className={['tabs__tab', selected ? 'tabs__tab--active' : ''].filter(Boolean).join(' ')}
            onClick={() => onChange(it.value)}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}
